// src/services/managedAccountService.js  (PATCH v2)
// ─────────────────────────────────────────────────────────────────────────────
// MUDANÇA v2: refatoração de estrutura e JSDoc para espelhar userGroupService.js.
//   - Nenhuma lógica, endpoint ou assinatura de função foi alterada.
//   - Seções agrupadas por categoria (Listagem, CRUD Individual, Lote, Utilitários).
//   - JSDoc padronizado com @param e @returns em todos os métodos.
// ─────────────────────────────────────────────────────────────────────────────
import api from "./api";

// ─────────────────────────────────────────────────────────────────────────────
// Listagem e Consulta
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Retorna todas as contas gerenciadas ativas do titular autenticado.
 * GET /api/managed-accounts
 * @returns {Promise} Lista de ManagedAccountReadDto
 */
export function fetchAll() {
  return api.get("/managed-accounts");
}

/**
 * Retorna vínculos paginados do titular autenticado (todos os status).
 * GET /api/managed-accounts/relationships/paged
 * @param {number} pageNumber - Número da página (base 1)
 * @param {number} pageSize   - Itens por página
 * @param {string} search     - Termo de busca opcional (nome, username ou e-mail)
 * @returns {Promise} { data, totalCount, currentPage, pageSize, totalPages }
 */
export function fetchPaged(pageNumber, pageSize, search = "") {
  return api.get("/managed-accounts/relationships/paged", {
    params: { pageNumber, pageSize, search },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Operações CRUD Individuais
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cria um novo usuário e o vincula como conta gerenciada do titular autenticado.
 * POST /api/managed-accounts
 * @param {object} data - ManagedAccountCreateDto
 * @returns {Promise} ManagedAccountReadDto criado
 */
export function create(data) {
  return api.post("/managed-accounts", data);
}

/**
 * Convida um usuário já existente para o círculo do titular autenticado. O
 * vínculo só nasce quando a pessoa aceita.
 * POST /api/managed-accounts/relationships
 * @param {object} data - ManagedAccountLinkDto
 * @returns {Promise} ManagedAccountInvitationReadDto criado
 */
export function linkExisting(data) {
  return api.post("/managed-accounts/relationships", data);
}

// ─────────────────────────────────────────────────────────────────────────────
// Convites (conta existente entra no círculo só depois de aceitar)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convites do círculo, mais recentes primeiro (todos os status).
 * GET /api/managed-accounts/invitations
 * @returns {Promise} Lista de ManagedAccountInvitationReadDto
 */
export function fetchInvitations() {
  return api.get("/managed-accounts/invitations");
}

/**
 * Lembra a pessoa por e-mail e renova o prazo de 7 dias (reabre o expirado).
 * POST /api/managed-accounts/invitations/{id}/resend
 * @param {number} id
 * @returns {Promise} ManagedAccountInvitationReadDto
 */
export function resendInvitation(id) {
  return api.post(`/managed-accounts/invitations/${id}/resend`);
}

/**
 * POST /api/managed-accounts/invitations/{id}/cancel
 * @param {number} id
 * @returns {Promise} ManagedAccountInvitationReadDto
 */
export function cancelInvitation(id) {
  return api.post(`/managed-accounts/invitations/${id}/cancel`);
}

/**
 * Alterna o status de um vínculo (Active / Inactive / Revoked).
 * PATCH /api/managed-accounts/{id}/status
 * @param {number} id     - ID do relacionamento
 * @param {object} data   - { status: 'Active' | 'Inactive' | 'Revoked' }
 * @returns {Promise} ManagedAccountReadDto atualizado
 */
export function updateStatus(id, data) {
  return api.patch(`/managed-accounts/${id}/status`, data);
}

/**
 * Revoga permanentemente um vínculo singular (soft-delete via status Revoked).
 * DELETE /api/managed-accounts/{id}
 * @param {number} id - ID do relacionamento
 * @returns {Promise}
 */
export function revokeById(id) {
  return api.delete(`/managed-accounts/${id}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Operações em Lote
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convida múltiplos usuários já existentes para o círculo (bulk por IDs).
 * POST /api/managed-accounts/relationships/bulk-add/by-pairs
 * @param {object} data - ManagedAccountBulkLinkDto
 * @returns {Promise} Array de ManagedAccountInvitationReadDto
 */
export function bulkLinkByPairs(data) {
  return api.post("/managed-accounts/relationships/bulk-add/by-pairs", data);
}

/**
 * Revoga permanentemente múltiplos vínculos pelo ID do relacionamento.
 * POST /api/managed-accounts/relationships/bulk-remove/by-ids
 * @param {object} data - { relationshipIds: number[] }
 * @returns {Promise}
 */
export function bulkRevokeByIds(data) {
  return api.post("/managed-accounts/relationships/bulk-remove/by-ids", data);
}

/**
 * Processa um lote misto de novas contas e vínculos de usuários existentes.
 * Falhas individuais são reportadas sem abortar os itens restantes.
 * POST /api/managed-accounts/batch
 * @param {Array<object>} items - Array de ManagedAccountBatchItemDto
 * @returns {Promise} ManagedAccountBatchResultDto { succeeded, failed }
 */
export function processBatch(items) {
  return api.post("/managed-accounts/batch", { items });
}

/**
 * Consulta prévia do lote: diz quais e-mails e nomes de usuário JÁ existem na
 * plataforma, sem criar nada.
 *
 * Existe por causa da importação de planilha. O lote é tudo-ou-nada, e colisão
 * com conta existente é a única falha que o cliente não detecta sozinho — sem
 * esta consulta, um e-mail repetido só aparece depois de a planilha inteira
 * falhar no envio.
 *
 * POST /api/managed-accounts/batch/precheck
 * @param {string[]} emails
 * @param {string[]} userNames
 * @param {string[]} groupNames - turmas da planilha: a resposta diz quais já
 *   existem como grupo do titular (serão reutilizadas) e quais têm nome
 *   reservado pelo sistema ("Todos").
 * @returns {Promise} { existingEmails, existingUserNames, existingGroupNames,
 *   reservedGroupNames } — os MESMOS valores enviados que colidiram, para
 *   casar direto com a linha da planilha.
 */
export function precheckBatch(emails = [], userNames = [], groupNames = []) {
  return api.post("/managed-accounts/batch/precheck", {
    emails,
    userNames,
    groupNames,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilitários
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Busca usuários por username ou e-mail para vincular como gerenciados.
 * GET /api/users/search
 * @param {string}   q          - Termo de busca (mín. 2 caracteres)
 * @param {number[]} excludeIds - IDs a excluir dos resultados
 * @returns {Promise} Array de usuários encontrados
 */
export function searchUsersForLink(q, excludeIds = []) {
  return api.get("/users/search", {
    params: { q, excludeIds: excludeIds.join(",") },
  });
}

/**
 * Retorna o consumo agregado do titular + gerenciados ativos.
 * GET /api/managed-accounts/usage
 * @returns {Promise} AggregatedUsageReadDto
 */
export function fetchAggregatedUsage() {
  return api.get("/managed-accounts/usage");
}

/**
 * Conclui o login após seleção de contexto (owner / managed).
 * POST /api/auth/login/select-context
 * @param {string}      sessionToken    - Token temporário de seleção de contexto
 * @param {number|null} relationshipId  - null = owner; número = managed
 * @returns {Promise} { token, user }
 */
export function selectLoginContext(sessionToken, relationshipId = null) {
  return api.post("/auth/login/select-context", {
    sessionToken,
    relationshipId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Permissões granulares por vínculo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Atualiza as permissões granulares de um vínculo Membro-Titular.
 * PATCH /api/managed-accounts/{id}/permissions
 * @param {number} relationshipId
 * @param {{ permissions: { texts, notes, activities } }} data
 */
export function updatePermissions(relationshipId, data) {
  return api.patch(`/managed-accounts/${relationshipId}/permissions`, data);
}

/**
 * Ids do círculo do contexto de login ativo: o titular e os membros ativos dele.
 * GET /api/managed-accounts/my-circle
 */
export function fetchMyCircle() {
  return api.get("/managed-accounts/my-circle");
}

/**
 * Moldes de permissão por tipo de vínculo ("Professor", "Aluno", ...).
 * Valem para todo membro daquele tipo que não tenha permissões próprias —
 * inclusive os cadastrados depois.
 * GET /api/managed-accounts/permission-templates
 */
export function fetchPermissionTemplates() {
  return api.get("/managed-accounts/permission-templates");
}

/**
 * Grava (cria ou substitui) o molde de um tipo de vínculo.
 * PUT /api/managed-accounts/permission-templates/{relationshipType}
 * @param {string} relationshipType
 * @param {{ texts, notes, activities, restrictToTitular, canManageCircle }} permissions
 */
export function upsertPermissionTemplate(relationshipType, permissions) {
  return api.put(
    `/managed-accounts/permission-templates/${encodeURIComponent(relationshipType)}`,
    permissions
  );
}

/**
 * Remove o molde de um tipo; os membros daquele tipo sem permissões próprias
 * voltam ao padrão do sistema.
 * DELETE /api/managed-accounts/permission-templates/{relationshipType}
 */
export function deletePermissionTemplate(relationshipType) {
  return api.delete(
    `/managed-accounts/permission-templates/${encodeURIComponent(relationshipType)}`
  );
}

/**
 * Resolve as permissões efetivas do usuário logado.
 * Retorna MemberPermissionsDto se for membro, null se for titular/comum.
 * GET /api/managed-accounts/my-permissions
 */
export function resolveMyPermissions() {
  return api.get("/managed-accounts/my-permissions");
}

/**
 * Retorna os usuários ligados ao usuário logado por vínculo ativo:
 * os titulares de quem ele é membro e os membros que ele gerencia.
 * GET /api/managed-accounts/my-related-users
 * @returns {Promise} Array de RelatedUserDto
 */
export function fetchMyRelatedUsers() {
  return api.get("/managed-accounts/my-related-users");
}

export function updateRelationship(id, data) {
  return api.patch(`/managed-accounts/${id}`, data);
}

// ─────────────────────────────────────────────────────────────────────────────
// Aliases de compatibilidade — mantidos para não quebrar chamadas existentes
// em ManagedAccountOnboarding.vue e outros componentes que ainda usam os
// nomes antigos. Estes aliases NÃO devem ser usados em código novo.
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use fetchAll() */
export const fetchManagedAccounts = fetchAll;

/** @deprecated Use fetchPaged() */
export const fetchManagedAccountsPaged = fetchPaged;

/** @deprecated Use create() */
export const createManagedAccount = create;

/** @deprecated Use linkExisting() */
export const linkExistingUser = linkExisting;

/** @deprecated Use bulkLinkByPairs() */
export const bulkLinkExistingUsers = bulkLinkByPairs;

/** @deprecated Use bulkRevokeByIds() */
export const bulkRevokeRelationships = bulkRevokeByIds;

/** @deprecated Use updateStatus() */
export const updateRelationshipStatus = updateStatus;

/** @deprecated Use revokeById() */
export const revokeRelationship = revokeById;
